import { useAuth, WS_STATUS } from "@/contexts/AuthContext";

/**
 * Free-vs-paid entitlements, mirrored from backend /workspaces/current.
 *
 * Fails CLOSED. Until the workspace has actually loaded we grant nothing —
 * previously these defaulted to permissive, which meant a free workspace
 * rendered every paid feature unlocked on each mount (and on each workspace
 * switch) until the fetch landed. The user saw a capability, clicked it, and
 * got a 402. Showing something as available and then taking it away is worse
 * than showing it as pending.
 *
 * Call sites must therefore branch on `loaded` and render a pending state
 * (a skeleton, or "--") rather than a locked/upsell state, or a paid user
 * briefly sees their own features advertised back at them.
 *
 * So every gate here is THREE states, never two:
 *   !loaded            → pending. Neutral. Claim nothing either way.
 *   loaded && allowed  → the real control.
 *   loaded && !allowed → the locked / upsell control.
 *
 * `<PendingButton>` in components/UpgradeLock.jsx is the neutral counterpart
 * to `<LockedButton>` for the first branch. Every field below except `loaded`
 * and `plan` is meaningless until `loaded` is true — do not read them before.
 *
 * ── !loaded IS NOT ONE STATE ────────────────────────────────────────────────
 * `loaded: false` used to mean two completely different things: "not fetched
 * yet" and "fetched and failed." The pending vocabulary above is honest for the
 * first and A LIE for the second — "Checking…" with aria-busy="true" while
 * nothing is being checked, forever. Call sites must now branch three ways:
 *
 *   pending  → still coming.   <PendingButton>  "Checking…"  aria-busy=true
 *   unknown  → stopped coming. <UnknownButton>  "Paused"     aria-busy=false
 *   loaded   → the real control, or the locked one.
 *
 * `unknown` deliberately does NOT unlock anything. Owner decision, 19 Jul: lock
 * ACTIONS, keep what's already loaded. The customer can read leads on screen,
 * navigate, and reach Billing and Settings; they cannot start a scan or save a
 * lead until the plan is confirmed. Nothing that costs money or consumes quota
 * runs on a guess.
 */
export function useEntitlements() {
  const { workspace, workspaceStatus } = useAuth();
  const e = workspace?.entitlements;
  const has = !!e;
  const unknown = !has && workspaceStatus === WS_STATUS.UNREACHABLE;
  return {
    loaded: has,
    /** Fetched and failed. Claim nothing; promise nothing. */
    unknown,
    /** Still in flight (or retrying) — "Checking…" is still true. */
    pending: !has && !unknown,
    status: workspaceStatus,
    isPaid: e?.is_paid ?? false,
    plan: e?.plan ?? workspace?.plan ?? "free",
    canScan: has ? e.can_scan : false,
    canImport: has ? e.can_import_brand : false,
    canReset: has ? e.can_reset : false,
    canSave: has ? e.can_save_lead : false,
    canViewSource: has ? e.can_view_source : false,
    canUseOtherLenses: has ? e.can_use_other_lenses : false,
    scanUsed: e?.scan_used ?? false,
    /**
     * Can this workspace create ANOTHER brand? Fails closed like every gate
     * above: before entitlements land we do not know, so we do not offer it.
     *
     * The backend computes this as `brands_used < brands_allowed` and now
     * refuses at GENERATE time with a 409, before the LLM spend — so this flag
     * and the server's refusal agree, rather than the client cheerfully
     * starting a two-minute job the server was always going to reject.
     *
     * `brandsAllowed` / `brandsUsed` are for the at-cap SENTENCE, not for
     * re-deriving the gate. Do not compute the boolean from them at a call
     * site: two answers to one question is how the pair drifts.
     */
    canCreateBrand: has ? e.can_create_brand : false,
    brandsAllowed: has ? e.brands_allowed : null,
    brandsUsed: has ? e.brands_used : null,
    // NOTE: there is deliberately no `importUsed`. The API does not report
    // whether the free brand import has been consumed, and the backend helper
    // that would compute it counts rows nothing writes. Don't add it back
    // without a writer — see the comment in backend/entitlements.py.
    //
    // Brand analysis (import / reset) is free but rate-limited to once / 30min.
    brandAnalysisAvailable: has ? e.brand_analysis_available : false,
    brandAnalysisUnblocksAt: e?.brand_analysis_unblocks_at ?? null,
  };
}
